/**
 * Watched brands per space. Loaded once on mount + on space change. Lets
 * any component (subnav, brand cards, brand page, library filters) read
 * the current watch list and toggle membership without each component
 * fetching independently.
 *
 * Source of truth lives in Mongo's advertiser_space collection
 * (spring-backend). This context is just the in-memory mirror + write
 * helpers that hit /intelligence/watch-list.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'

import * as API from '@/services/api.js'


const WatchListContext = createContext(null)


export function WatchListProvider({ children }) {

    const { spaceId } = useParams()
    const [brands, setBrands] = useState([])
    // Start in the loading state when a space is present so consumers never
    // read an empty, not-yet-fetched list as "watches nothing" on first render.
    const [isLoading, setIsLoading] = useState(() => Boolean(spaceId))

    const refresh = useCallback(async () => {
        if (!spaceId) {
            setBrands([])
            setIsLoading(false)
            return
        }
        setIsLoading(true)
        try {
            const { data } = await API.listWatchedBrands()
            setBrands(data?.brands ?? [])
        } catch {
            setBrands([])
        } finally {
            setIsLoading(false)
        }
    }, [spaceId])

    useEffect(() => {
        refresh()
    }, [refresh])

    const watchedIds = useMemo(() => new Set(brands.map((b) => b.advertiser_id)), [brands])

    const isWatching = useCallback((advertiserId) => {
        return watchedIds.has(advertiserId)
    }, [watchedIds])

    // Optimistic toggle — flip the local state immediately, fire the API
    // call in the background. On error, revert.
    const watch = useCallback(async (brand) => {
        if (!brand?.advertiser_id || watchedIds.has(brand.advertiser_id)) {
            return
        }
        // Prepend so newest-watched sits at top in the subnav list.
        setBrands((prev) => [{
            advertiser_id: brand.advertiser_id,
            display_name: brand.display_name ?? null,
            profile_picture_url: brand.profile_picture_url ?? null,
        }, ...prev])
        try {
            await API.watchBrand(brand.advertiser_id)
        } catch (e) {
            // Revert on failure
            setBrands((prev) => prev.filter((b) => b.advertiser_id !== brand.advertiser_id))
            throw e
        }
    }, [watchedIds])

    const unwatch = useCallback(async (advertiserId) => {
        if (!watchedIds.has(advertiserId)) {
            return
        }
        const snapshot = brands
        setBrands((prev) => prev.filter((b) => b.advertiser_id !== advertiserId))
        try {
            await API.unwatchBrand(advertiserId)
        } catch (e) {
            setBrands(snapshot)
            throw e
        }
    }, [brands, watchedIds])

    const value = useMemo(() => ({
        brands,
        isLoading,
        isWatching,
        watch,
        unwatch,
        refresh,
    }), [brands, isLoading, isWatching, watch, unwatch, refresh])

    return <WatchListContext.Provider value={value}>{children}</WatchListContext.Provider>
}


export function useWatchList() {
    const ctx = useContext(WatchListContext)
    if (!ctx) {
        // Returning safe defaults instead of throwing so the subnav etc.
        // don't crash if rendered outside the provider during a transition.
        return {
            brands: [],
            isLoading: false,
            isWatching: () => false,
            watch: async () => {},
            unwatch: async () => {},
            refresh: async () => {},
        }
    }
    return ctx
}
